import { useEffect,useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";

const emptyForm={
  nombres:"",
  apellidos:"",
  identificacion:"",
  correo:"",
  telefono:"",
  active:true,
};

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

export default function TeamDelegateModal({
  open,
  onClose,
  onSaved,
  delegate,
}){
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [form,setForm]=useState(emptyForm);
  const [errors,setErrors]=useState({});
  const [saving,setSaving]=useState(false);

  useEffect(()=>{
    if(!open) return;

    if(delegate){
      setForm({
        nombres:delegate.nombres||"",
        apellidos:delegate.apellidos||"",
        identificacion:delegate.identificacion||"",
        correo:delegate.correo||"",
        telefono:delegate.telefono||"",
        active:delegate.active!==false,
      });
    }else{
      setForm(emptyForm);
    }

    setErrors({});
  },[open,delegate]);

  const handleChange=(field,value)=>{
    setForm((current)=>({
      ...current,
      [field]:value,
    }));

    setErrors((current)=>({
      ...current,
      [field]:"",
    }));
  };

  const validate=()=>{
    const nextErrors={};

    if(!form.nombres.trim()){
      nextErrors.nombres="Ingresa los nombres";
    }

    if(!form.apellidos.trim()){
      nextErrors.apellidos="Ingresa los apellidos";
    }

    if(!form.identificacion.trim()){
      nextErrors.identificacion="Ingresa la identificación";
    }

    if(!form.correo.trim()){
      nextErrors.correo="Ingresa el correo";
    }else if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.correo.trim())){
      nextErrors.correo="Ingresa un correo válido";
    }

    if(!form.telefono.trim()){
      nextErrors.telefono="Ingresa el teléfono";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length===0;
  };

  const handleSubmit=async()=>{
    if(!validate()) return;

    try{
      setSaving(true);

      const editing=Boolean(delegate);
      const originalIdentification=delegate?.identificacion;

      const payload={
        nombres:form.nombres.trim(),
        apellidos:form.apellidos.trim(),
        identificacion:form.identificacion.trim(),
        correo:form.correo.trim().toLowerCase(),
        telefono:form.telefono.trim(),
      };

      if(editing){
        payload.active=form.active;
      }

      const response=await fetch(
        editing
          ? `/api/delegates/${encodeURIComponent(originalIdentification)}/`
          : "/api/delegates/",
        {
          method:editing?"PATCH":"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken"),
          },
          body:JSON.stringify(payload),
        }
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(
          data.error||
          Object.values(data).flat().join(" ")||
          "No se pudo guardar el delegado."
        );
      }

      await onSaved(data);

      showSuccess(
        editing
          ?"Delegado actualizado correctamente."
          :"Delegado creado correctamente."
      );
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  const handleClose=()=>{
    if(saving) return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving?undefined:handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx:{
          borderRadius:shape.radiusLarge,
          boxShadow:"none",
          border:`1px solid ${colors.border}`,
          backgroundColor:colors.surface,
          m:{xs:1.5,sm:3},
          width:{xs:"calc(100% - 24px)",sm:"100%"},
        },
      }}
    >
      <DialogTitle
        sx={{
          px:{xs:2,sm:2.5},
          pt:2.3,
          pb:1,
        }}
      >
        <Typography
          sx={{
            fontSize:typography.sizes.xl,
            fontWeight:typography.weights.extraBold,
            color:colors.text,
          }}
        >
          {delegate?"Editar delegado":"Nuevo delegado"}
        </Typography>

        <Typography
          sx={{
            mt:0.4,
            fontSize:typography.sizes.sm,
            color:colors.textSecondary,
          }}
        >
          {delegate
            ?"Actualiza la información de la cuenta del delegado."
            :"Registra la cuenta que utilizará el delegado para ingresar al sistema."
          }
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          px:{xs:2,sm:2.5},
          pt:"14px !important",
        }}
      >
        <Box
          sx={{
            display:"grid",
            gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},
            gap:1.5,
          }}
        >
          <TextField
            size="small"
            label="Nombres"
            value={form.nombres}
            onChange={(event)=>handleChange("nombres",event.target.value)}
            error={Boolean(errors.nombres)}
            helperText={errors.nombres}
            fullWidth
            autoFocus
          />

          <TextField
            size="small"
            label="Apellidos"
            value={form.apellidos}
            onChange={(event)=>handleChange("apellidos",event.target.value)}
            error={Boolean(errors.apellidos)}
            helperText={errors.apellidos}
            fullWidth
          />

          <TextField
            size="small"
            label="Identificación"
            value={form.identificacion}
            onChange={(event)=>handleChange("identificacion",event.target.value)}
            error={Boolean(errors.identificacion)}
            helperText={errors.identificacion}
            fullWidth
          />

          <TextField
            size="small"
            label="Teléfono"
            value={form.telefono}
            onChange={(event)=>handleChange("telefono",event.target.value)}
            error={Boolean(errors.telefono)}
            helperText={errors.telefono}
            fullWidth
          />

          <TextField
            size="small"
            label="Correo"
            type="email"
            value={form.correo}
            onChange={(event)=>handleChange("correo",event.target.value)}
            error={Boolean(errors.correo)}
            helperText={errors.correo}
            fullWidth
            sx={{
              gridColumn:{xs:"auto",sm:"1 / -1"},
            }}
          />

          {delegate&&(
            <FormControlLabel
              sx={{
                gridColumn:{xs:"auto",sm:"1 / -1"},
                ml:0,
                color:colors.text,
              }}
              control={
                <Switch
                  checked={form.active}
                  onChange={(event)=>handleChange("active",event.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked":{
                      color:colors.controlActive,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":{
                      backgroundColor:colors.controlActive,
                    },
                  }}
                />
              }
              label={form.active?"Delegado activo":"Delegado inactivo"}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px:{xs:2,sm:2.5},
          py:2,
          borderTop:`1px solid ${colors.border}`,
          gap:1,
        }}
      >
        <Button
          size="small"
          onClick={handleClose}
          disabled={saving}
          sx={{
            color:colors.textSecondary,
          }}
        >
          Cancelar
        </Button>

        <Button
          size="small"
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          sx={{
            backgroundColor:colors.secondary,
            color:colors.tableHeaderText,
            "&:hover":{
              backgroundColor:colors.secondaryHover,
            },
          }}
        >
          {saving
            ?"Guardando..."
            :delegate
              ?"Guardar cambios"
              :"Crear delegado"
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}